import { Component, OnInit } from '@angular/core';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';
import { TranslationPipe } from '../../../pipes/translation.pipe';
import {
  CustomerClient,
  CustomerDto,
  Gender, NeuteringStatus,
  PetBehavior,
  PetClient, PetDto, UpdatePetRequestDto, Veterinarian
} from '../../../api/api-client';
import { ActivatedRoute, Router } from '@angular/router';
import { FeedbackDataService } from '../../../services/feedback-data.service';
import { DateTime } from 'luxon';
import { FeedbackData } from '../../../models/feedback-data';
import { Editor } from 'primeng/editor';
import { conditionalRequiredValidator } from '../../../validators/conditional-required.validator';
import { datetimeNotInFutureValidator } from '../../../validators/datetime-not-in-future.validator';
import { isFormControlInvalid } from '../../../util/form-control-invalid-checker';
import { SubscriptionManager } from '../../../services/subscription-manager.service';

@Component({
  selector: 'app-edit-pet',
  imports: [
    AutoComplete,
    Button,
    DatePicker,
    InputMask,
    InputText,
    ReactiveFormsModule,
    Select,
    SelectButton,
    TranslationPipe,
    Editor
  ],
  providers: [SubscriptionManager],
  templateUrl: './edit-pet.component.html',
  styleUrl: './edit-pet.component.scss'
})
export class EditPetComponent implements OnInit {

  editPetForm = new FormGroup({
    owner: new FormControl<CustomerDto | null>(null, [Validators.required]),
    species: new FormControl<string>('', [Validators.required]),
    nickname: new FormControl<string>('', [Validators.required]),
    chipNumber: new FormControl<string>(''),
    race: new FormControl<string>('', [Validators.required]),
    color: new FormControl<string>(''),
    gender: new FormControl<Gender>('Unknown', [Validators.required]),
    dateOfBirth: new FormControl<Date>(DateTime.now().toJSDate(), datetimeNotInFutureValidator()),
    behavior: new FormControl<PetBehavior>('Unknown', [Validators.required]),
    behaviorNotes: new FormControl<string>(''),
    anamnesis: new FormControl(''),
    neuteringStatus: new FormControl<NeuteringStatus>('Unknown', [Validators.required]),
    isInsured: new FormControl<boolean>(false),
    insuranceProvider: new FormControl<string | null>(null),
    insuranceNumber: new FormControl<string | null>(null),
    hasHomeVet: new FormControl<boolean>(false),
    homeVetName: new FormControl<string | null>(null),
    homeVetPhoneNumber: new FormControl<string | null>(''),
    hasBreederGivenName: new FormControl<boolean | null>(false),
    breederGivenName: new FormControl<string | null>(''),
    firstTreatmentDate: new FormControl<Date>(DateTime.now().toJSDate())
  });

  customers!: CustomerDto[];
  customersSuggestions!: CustomerDto[];
  species = ['Dog', 'Cat'];
  genders = ['Unknown', 'Female', 'Male'];
  behaviors = ['Unknown', 'HighDanger', 'MediumDanger', 'LowDanger', 'PerfectlySafe'];
  neuteredStatusForms: any[] = [];
  boolOptions: any[] = [];

  petId!: string;
  pet!: PetDto;

  submitButtonClicked: boolean = false;

  constructor(
    private readonly subscriptionManager: SubscriptionManager,
    private readonly customerService: CustomerClient,
    private readonly route: ActivatedRoute,
    private readonly petService: PetClient,
    private readonly router: Router,
    private readonly feedbackService: FeedbackDataService,
    private readonly translationPipe: TranslationPipe
  ) {
    this.neuteredStatusForms = [
      { label: this.translationPipe.transform('Pet.NeuteringStatus.Neutered'), value: 'Neutered' },
      { label: this.translationPipe.transform('Pet.NeuteringStatus.NotNeutered'), value: 'NotNeutered' },
      { label: this.translationPipe.transform('Pet.NeuteringStatus.Unknown'), value: 'Unknown' }
    ];
    this.subscriptionManager.call(
      this.route.paramMap, (paramMap => {
        this.petId = paramMap.get('petId') ?? '';
      })
    );
    this.boolOptions = [{
      label: this.translationPipe.transform('Yes'),
      value: true
    }, { label: this.translationPipe.transform('No'), value: false }];

    this.editPetForm.get('insuranceProvider')?.setValidators(conditionalRequiredValidator(this.editPetForm, 'isInsured'));
    this.editPetForm.get('insuranceNumber')?.setValidators(conditionalRequiredValidator(this.editPetForm, 'isInsured'));
    this.editPetForm.get('homeVetName')?.setValidators(conditionalRequiredValidator(this.editPetForm, 'hasHomeVet'));
    this.editPetForm.get('homeVetPhoneNumber')?.setValidators([conditionalRequiredValidator(this.editPetForm, 'hasHomeVet'), Validators.pattern('[- +()0-9]{6,}')]);
    this.editPetForm.get('breederGivenName')?.setValidators(conditionalRequiredValidator(this.editPetForm, 'hasBreederGivenName'));

    // Trigger validation when isInsured changes
    this.subscriptionManager.call(
      this.editPetForm.get('isInsured')?.valueChanges,
      (() => {
        this.editPetForm.get('insuranceProvider')?.updateValueAndValidity();
        this.editPetForm.get('insuranceNumber')?.updateValueAndValidity();
      })
    );

    // Trigger validation when hasHomeVet changes
    this.subscriptionManager.call(
      this.editPetForm.get('hasHomeVet')?.valueChanges,
      (() => {
        this.editPetForm.get('homeVetName')?.updateValueAndValidity();
        this.editPetForm.get('homeVetPhoneNumber')?.updateValueAndValidity();
      })
    );

    // Trigger validation when hasBreeder changes
    this.subscriptionManager.call(
      this.editPetForm.get('hasBreederGivenName')?.valueChanges,
      (() => {
        this.editPetForm.get('breederGivenName')?.updateValueAndValidity();
      })
    );

  }


  ngOnInit() {
    this.subscriptionManager.call(
      this.petService.getPetById(this.petId),
      (petResult => {
        this.pet = petResult.result;
        this.subscriptionManager.call(
          this.customerService.getCustomerById(this.pet.ownerId),
          (customerResult => {
            this.editPetForm.setValue({
              species: this.pet.species,
              nickname: this.pet.nickname,
              chipNumber: this.pet.chipNumber,
              race: this.pet.race,
              color: this.pet.color,
              gender: this.pet.gender,
              dateOfBirth: this.pet.dateOfBirth.toJSDate(),
              behavior: this.pet.behavior,
              behaviorNotes: this.pet.behaviorText,
              anamnesis: this.pet.anamnesis,
              neuteringStatus: this.pet.neuteringStatus,
              breederGivenName: this.pet.breederGivenName ?? '',
              hasBreederGivenName: this.pet.hasBreederGivenName ?? false,
              hasHomeVet: this.pet.hasHomeVet,
              homeVetName: this.pet.homeVet?.name ?? '',
              insuranceProvider: this.pet.insuranceProvider,
              homeVetPhoneNumber: this.pet.homeVet?.phoneNumber ?? '',
              insuranceNumber: this.pet.insuranceNumber,
              isInsured: this.pet.isInsured,
              owner: customerResult.result,
              firstTreatmentDate: this.pet.firstTreatmentDate?.toJSDate() ?? DateTime.now().toJSDate()
            });
          })
        );
      })
    );
    this.subscriptionManager.call(
      this.customerService.getAllCustomersForTenant(),
      (x => {
        this.customers = x.result;
        this.customersSuggestions = x.result;
      })
    );
    this.feedbackService.feedbackData = new FeedbackData();
    this.feedbackService.feedbackData.petId = this.petId;
  }


  filterCustomers($event: AutoCompleteCompleteEvent) {
    this.customersSuggestions = this.customers.filter(x => (x.firstName + x.lastName).toLocaleLowerCase().includes($event.query.toLocaleLowerCase()));
  }

  getClassForBehavior(item: 'Unknown' | 'PerfectlySafe' | 'LowDanger' | 'MediumDanger' | 'HighDanger') {
    switch (item) {
      case 'Unknown':
        return ' bg-black w-8 h-8 rounded-full';
      case 'PerfectlySafe':
        return 'bg-pink-400 w-8 h-8 rounded-full';
      case 'LowDanger':
        return 'bg-green-400 w-8 h-8 rounded-full';
      case 'MediumDanger':
        return 'bg-yellow-400 w-8 h-8 rounded-full';
      case 'HighDanger':
        return 'bg-red-400 w-8 h-8 rounded-full';
    }
  }

  onSubmit() {
    this.submitButtonClicked = true;
    if (this.editPetForm.valid) {
      this.subscriptionManager.call(
        this.petService.updatePet(
          {
            id: this.petId,
            nickname: this.editPetForm.value.nickname ?? '',
            behavior: this.editPetForm.value.behavior ?? 'Unknown',
            color: this.editPetForm.value.color ?? '',
            race: this.editPetForm.value.race ?? '',
            species: this.editPetForm.value.species ?? '',
            gender: this.editPetForm.value.gender ?? 'Unknown',
            chipNumber: this.editPetForm.value.chipNumber?.replace(/\D/gi, '') ?? '',
            dateOfBirth: DateTime.fromJSDate(this.editPetForm.value.dateOfBirth ?? new Date(Date.now())),
            ownerId: this.editPetForm.value.owner?.id,
            anamnesis: this.editPetForm.value.anamnesis ?? '',
            behaviorText: this.editPetForm.value.behaviorNotes ?? '',
            neuteringStatus: this.editPetForm.value.neuteringStatus ?? 'Unknown',
            isInsured: this.editPetForm.value.isInsured ?? false,
            insuranceProvider: this.editPetForm.value.insuranceProvider ?? '',
            insuranceNumber: this.editPetForm.value.insuranceNumber ?? '',
            hasHomeVet: this.editPetForm.value.hasHomeVet ?? false,
            hasBreeder: this.editPetForm.value.hasBreederGivenName ?? false,
            breederGivenName: this.editPetForm.value.breederGivenName ?? '',
            homeVet: <Veterinarian><unknown>{
              name: this.editPetForm.value.homeVetName ?? '',
              phoneNumber: this.editPetForm.value.homeVetPhoneNumber ?? ''
            },
            firstTreatmentDate: DateTime.fromJSDate(this.editPetForm.value.firstTreatmentDate ?? new Date(Date.now()))
          } as unknown as UpdatePetRequestDto
        ),
        (x => {
          this.router.navigate(['../loggedin/pet-detail/' + this.petId]);
        })
      );
    }
  }


  getOptionLabelForOwner(owner: CustomerDto): string {
    return owner.firstName + ' ' + owner.lastName + ', ' + owner.street + ' ' + owner.houseNumber + ', ' + owner.postalCode + ' ' + owner.city + ', ' + owner.country;
  }

  protected abortEdit() {
    this.router.navigate(['../loggedin/pet-detail/' + this.petId]);
  }

  protected readonly isFormControlInvalid = isFormControlInvalid;
}
